/**
 * Coups de pouce — Attendu 901 « Diviser par un nombre décimal ».
 * Une entrée par generator_key (cf. gen_901.js), cinq cartes, six fiches.
 *
 * ─── Forme, reprise telle quelle de la revue 900 ───────────────────────────
 *   `court`  → l'idée-clé, une phrase (rendue en gras avec 💡)
 *   `lignes` → DEUX étapes, numérotées par la sheet
 *   `svg`    → le schéma seul, sans texte de règle
 * Seuls `court` et `lignes` sont lus à voix haute par CoupDePouceSheet : une
 * fiche dont la règle vit dans le SVG est MUETTE. Le schéma garde son
 * `aria-label` pour les lecteurs d'écran.
 *
 * ─── Deux fiches à UNE page ────────────────────────────────────────────────
 * `transformer` : la page 2 montrait le piège « ×10 sur le seul numérateur ».
 * Jugée sans utilité (retour utilisateur, 2026-08-02) — l'énoncé nomme
 * explicitement les deux termes, « je multiplie le numérateur ET le
 * dénominateur par … ». Reste la page 1, qui porte le vrai travail de la carte
 * depuis que le multiplicateur y est demandé et non plus affiché : compter les
 * décimales pour choisir entre 10, 100 et 1 000.
 *
 * ─── Pourquoi « multiplier le moins » a DEUX fiches ────────────────────────
 * Les deux formats ne demandent pas le même travail :
 *   - le QCM s'arrête au multiplicateur (« quel est le plus petit entier ? ») ;
 *   - le texte à trous l'applique ensuite aux deux nombres et pose la division.
 * Le QCM n'a donc qu'UNE page, la même que la page 1 du texte à trous
 * (`PAGE_MULTIPLICATEUR`, partagée par identité). Les deux `generator_key`
 * étant distincts, aucun routage par `sousType` n'est nécessaire.
 *
 * ─── Les valeurs restent HORS des plages de tirage ─────────────────────────
 * Sans quoi une fiche servirait la réponse d'un exercice ouvert. Vérifié
 * générateur en main :
 *   - transformer : 125,4 (il ne dépasse jamais 99) et 0,8 (ses numérateurs
 *     sont toujours ≥ 1, le cas < 1 n'existe pas) ;
 *   - quotients : 7,2 ÷ 0,45 (il tire N entre 11,1 et 98,9, D entre 1,1 et 9,8,
 *     tous deux à UNE décimale) ;
 *   - multiplier le moins : 0,25 / 0,2 / 2,3 / 7,5 (aucun n'est dans les 30
 *     diviseurs du vivier, qui va de 0,3 à 6,5) ;
 *   - bouteilles : 0,35 L (les 14 contenances tirées n'incluent que des
 *     multiples de 5 centilitres « ronds », jamais 0,35) ;
 *   - rectangles : 6,4 cm et coefficient 1,25 (AB plafonne à 5,6 cm et les
 *     coefficients tirés sont 1,5 / 2 / 2,5 / 3 / 3,5).
 * Aucune lettre de sommet sur la figure des rectangles : le générateur fait
 * tourner quatre jeux (ABCD, IJKL, RSTU, EFGH), une fiche figée sur « AB »
 * contredirait la moitié des tirages.
 *
 * ─── Animation ────────────────────────────────────────────────────────────
 * Trois invariants repris des revues 899 et 900 : l'animation se FIGE une fois
 * terminée, un bouton « ↻ Revoir » la relance, la dernière étape à apparaître
 * est la conclusion. D'où SMIL plutôt que des @keyframes CSS — le SVG est
 * injecté par dangerouslySetInnerHTML, aucun JS ne s'y exécute, seul
 * `begin="0s;btn.click"` permet un rejeu et `fill="freeze"` un arrêt sur image.
 *
 * ⚠ Géométrie : les neuf figures passent une sonde qui reconstitue la boîte de
 * chaque <text> et vérifie qu'elle tient dans le viewBox et ne recouvre ni le
 * bouton « Revoir » ni un autre texte. Toute retouche est à re-mesurer.
 */

/**
 * Étape d'apparition : le groupe reste transparent jusqu'à `p` (fraction du
 * cycle), monte en 3 % puis GÈLE. Tous les groupes d'une figure partagent le
 * même `begin`, donc un clic sur le bouton rejoue toute la cascade.
 */
const apparition = (p, btn, duree = '7.6s') =>
    `<animate attributeName="opacity" values="0;0;1;1" ` +
    `keyTimes="0;${p.toFixed(2)};${(p + 0.03).toFixed(2)};1" ` +
    `dur="${duree}" begin="0s;${btn}.click" fill="freeze"/>`;

/** Groupe animé. opacity="1" en attribut = repli si SMIL est indisponible. */
const etape = (p, btn, contenu) =>
    `<g class="anim" opacity="1">${apparition(p, btn)}${contenu}</g>`;

const bouton = (id, x, y) =>
    `<g id="${id}" style="cursor:pointer">` +
    `<title>Rejouer l'animation</title>` +
    `<rect x="${x}" y="${y}" width="76" height="21" rx="10.5" fill="#EFF6FF" stroke="#1E3A8A" stroke-width="1.2"/>` +
    `<text x="${x + 38}" y="${y + 15}" text-anchor="middle" font-size="11" font-weight="bold" fill="#1E3A8A">↻ Revoir</text>` +
    `</g>`;

// Le seul CSS restant : respecter prefers-reduced-motion. En cas d'échec de la
// règle, l'élève voit l'animation, jamais une figure vide.
const STYLE_REDUCED = `<style>@media (prefers-reduced-motion: reduce) { .anim { opacity: 1 !important; } }</style>`;

const BLEU = '#1E3A8A', ORANGE = '#f97316', ARDOISE = '#334155', ENCRE = '#0f172a';
const GRIS = '#94a3b8', VERT = '#16a34a', ROUGE = '#dc2626';

/** Filet de séparation entre les deux scènes d'une même figure. */
const filet = (y) => `<line x1="14" y1="${y}" x2="286" y2="${y}" stroke="#e2e8f0" stroke-width="1.2"/>`;

const titre = (txt) => `<text x="14" y="19" font-size="13" font-weight="bold" fill="${ENCRE}">${txt}</text>`;

const coche = (x, y) => `<text x="${x}" y="${y}" font-size="15" font-weight="bold" fill="${VERT}">✓</text>`;
const croix = (x, y) => `<text x="${x}" y="${y}" font-size="15" font-weight="bold" fill="${ROUGE}">✗</text>`;

/** Fraction centrée sur `cx`, barre à `yBar`. */
const fraction = (cx, yBar, haut, bas, demi, taille = 12.5, couleur = ENCRE) =>
    `<text x="${cx}" y="${yBar - 6}" text-anchor="middle" font-size="${taille}" font-weight="bold" fill="${couleur}">${haut}</text>` +
    `<line x1="${cx - demi}" y1="${yBar}" x2="${cx + demi}" y2="${yBar}" stroke="${couleur}" stroke-width="1.6"/>` +
    `<text x="${cx}" y="${yBar + 16}" text-anchor="middle" font-size="${taille}" font-weight="bold" fill="${couleur}">${bas}</text>`;

/** Flèche horizontale, légende au-dessus. */
const fleche = (x1, x2, y, label = '', couleur = ORANGE) =>
    `<line x1="${x1}" y1="${y}" x2="${x2 - 6}" y2="${y}" stroke="${couleur}" stroke-width="1.6"/>` +
    `<path d="M ${x2} ${y} L ${x2 - 7} ${y - 4} L ${x2 - 7} ${y + 4} Z" fill="${couleur}"/>` +
    (label
        ? `<text x="${(x1 + x2) / 2}" y="${y - 7}" text-anchor="middle" font-size="10" font-weight="bold" fill="${couleur}">${label}</text>`
        : '');

const egal = (x, y) => `<text x="${x}" y="${y}" text-anchor="middle" font-size="13" font-weight="bold" fill="${ARDOISE}">=</text>`;

// ═══════════════════════════════════════════════════════════
// Fiche 1 — Transformer en fraction de nombres entiers
// ═══════════════════════════════════════════════════════════

const BTN_A = 'cp901rejouerA';

/**
 * Une chaîne « fraction décimale = fraction avec le ×10^k écrit = fraction
 * entière ». C'est l'étape intermédiaire que l'exercice n'affiche PAS : c'est
 * justement ce qu'il demande de trouver, et le rôle de la fiche est de la
 * montrer une fois, sur d'autres valeurs.
 */
const chaine = (yBar, num, den, mult, numE, denE) =>
    fraction(52, yBar, num, den, 24) +
    egal(100, yBar + 4) +
    fraction(168, yBar, `${num} × ${mult}`, `${den} × ${mult}`, 40, 11, BLEU) +
    egal(216, yBar + 4) +
    fraction(250, yBar, numE, denE, 22, 12.5, ORANGE);

const SVG_CHASSER = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 196" width="290" height="190" role="img"
     aria-label="Deux exemples de transformation. Premier : la fraction 125,4 sur 3,8 ; le nombre le plus décimal en a une seule, on multiplie donc le numérateur et le dénominateur par 10, ce qui donne 1254 sur 38. Second : la fraction 0,8 sur 1,25 ; 1,25 a deux décimales, on multiplie tout par 100, ce qui donne 80 sur 125. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  ${titre('Chasser les virgules')}
  ${bouton(BTN_A, 210, 4)}
  ${etape(0.06, BTN_A, chaine(64, '125,4', '3,8', '10', '1254', '38'))}
  ${etape(0.30, BTN_A, `<text x="52" y="96" text-anchor="middle" font-size="9.5" font-weight="bold" fill="${ORANGE}">1 décimale</text>`)}
  ${etape(0.42, BTN_A, filet(110))}
  ${etape(0.50, BTN_A, chaine(152, '0,8', '1,25', '100', '80', '125'))}
  ${etape(0.76, BTN_A, `<text x="52" y="184" text-anchor="middle" font-size="9.5" font-weight="bold" fill="${ORANGE}">2 décimales</text>`)}
</svg>`.trim();

/**
 * Une seule page. La page 2 montrait le piège « ×10 sur le seul numérateur » ;
 * depuis que la phrase de l'exercice affiche l'étape intermédiaire, le « × 10 »
 * est écrit sur les DEUX termes sous les yeux de l'élève et cette erreur n'est
 * plus atteignable. Elle n'avait donc plus rien à enseigner (retour
 * utilisateur, 2026-08-02).
 */
const FICHE_TRANSFORMER = {
    pages: [
        {
            titre: 'Chasser les virgules',
            court: 'Je multiplie le numérateur et le dénominateur par la même puissance de 10.',
            lignes: [
                'Je compte les décimales de chacun et je garde **le plus grand**.',
                '0,8 a 1 décimale, 1,25 en a 2 : je multiplie tout par **100**.',
            ],
            svg: SVG_CHASSER,
        },
    ],
};

// ═══════════════════════════════════════════════════════════
// Fiche 2 — Quotients égaux
// ═══════════════════════════════════════════════════════════

const BTN_C = 'cp901rejouerC', BTN_D = 'cp901rejouerD';

const SVG_GLISSEMENT = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 186" width="290" height="180" role="img"
     aria-label="Deux comparaisons avec le quotient 7,2 divisé par 0,45. En haut, on multiplie les deux nombres par 100 : cela donne 720 divisé par 45, la virgule a glissé de deux rangs des deux côtés, le quotient est le même, coche verte. En bas, on multiplie le premier par 100 mais le second par 10 seulement : cela donne 720 divisé par 4,5, croix rouge, le résultat vaut 160 au lieu de 16. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  ${titre('Le même glissement')}
  ${bouton(BTN_C, 210, 4)}
  ${etape(0.06, BTN_C, `<text x="20" y="56" font-size="13.5" font-weight="bold" fill="${ENCRE}">7,2 ÷ 0,45</text>`)}
  ${etape(0.18, BTN_C, fleche(110, 150, 52, '× 100'))}
  ${etape(0.28, BTN_C,
    `<text x="160" y="56" font-size="13.5" font-weight="bold" fill="${VERT}">720 ÷ 45</text>` + coche(236, 57))}
  ${etape(0.38, BTN_C,
    `<text x="150" y="78" text-anchor="middle" font-size="9.5" fill="${ARDOISE}">les deux ont glissé de 2 rangs</text>`)}
  ${etape(0.46, BTN_C, filet(92))}
  ${etape(0.54, BTN_C, `<text x="20" y="128" font-size="13.5" font-weight="bold" fill="${ENCRE}">7,2 ÷ 0,45</text>`)}
  ${etape(0.64, BTN_C,
    fleche(108, 152, 124) +
    `<text x="130" y="114" text-anchor="middle" font-size="9.5" font-weight="bold" fill="${ORANGE}">× 100</text>` +
    `<text x="130" y="140" text-anchor="middle" font-size="9.5" font-weight="bold" fill="${ROUGE}">× 10</text>`)}
  ${etape(0.74, BTN_C,
    `<text x="160" y="128" font-size="13.5" font-weight="bold" fill="${ROUGE}">720 ÷ 4,5</text>` + croix(238, 129))}
  ${etape(0.84, BTN_C,
    `<text x="150" y="150" text-anchor="middle" font-size="9.5" fill="${ARDOISE}">le bas n'a glissé que d'un rang</text>`)}
  ${etape(0.92, BTN_C,
    `<text x="150" y="172" text-anchor="middle" font-size="11.5" font-weight="bold" fill="${ROUGE}">720 ÷ 4,5 = 160, pas 16</text>`)}
</svg>`.trim();

/** Une ligne du tableau de tri : l'écriture, le mouvement des virgules, le verdict. */
const bandeTri = (y, expr, mouvement, ok) =>
    `<rect x="14" y="${y}" width="272" height="36" rx="7" fill="#f8fafc" stroke="#cbd5e1" stroke-width="1.2"/>` +
    `<text x="26" y="${y + 16}" font-size="11.5" font-weight="bold" fill="${BLEU}">${expr}</text>` +
    `<text x="26" y="${y + 30}" font-size="9.5" fill="${ARDOISE}">${mouvement}</text>` +
    (ok ? coche(262, y + 25) : croix(262, y + 25));

const SVG_TRIER = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 194" width="290" height="188" role="img"
     aria-label="Trois écritures comparées à la référence 7,2 divisé par 0,45. Première, 72 divisé par 4,5 : chaque nombre a avancé d'un rang, elle est égale, coche verte. Deuxième, 0,72 divisé par 0,045 : chaque nombre a reculé d'un rang, elle est égale, coche verte. Troisième, 72 divisé par 0,45 : seul le premier a bougé, elle est différente, croix rouge. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  ${titre('Égal ou différent ?')}
  ${bouton(BTN_D, 210, 4)}
  ${etape(0.06, BTN_D,
    `<text x="14" y="42" font-size="11" fill="${ARDOISE}">Référence : <tspan font-weight="bold" fill="${ENCRE}">7,2 ÷ 0,45</tspan></text>`)}
  ${etape(0.22, BTN_D, bandeTri(52, '72 ÷ 4,5', 'chacun avance d\'un rang', true))}
  ${etape(0.44, BTN_D, bandeTri(98, '0,72 ÷ 0,045', 'chacun recule d\'un rang', true))}
  ${etape(0.66, BTN_D, bandeTri(144, '72 ÷ 0,45', 'seul le premier a bougé', false))}
</svg>`.trim();

const FICHE_QUOTIENTS = {
    pages: [
        {
            titre: 'Le même glissement des deux côtés',
            court: 'Deux quotients sont égaux si la virgule glisse pareil dans les deux nombres.',
            lignes: [
                '**7,2 ÷ 0,45** : je glisse de 2 rangs partout, ça fait 720 ÷ 45.',
                'Un rang d\'un côté, deux de l\'autre : le quotient a changé.',
            ],
            svg: SVG_GLISSEMENT,
        },
        {
            titre: 'Égal ou différent ?',
            court: 'Pas besoin de poser la division : je compare les deux glissements.',
            lignes: [
                'Je repère de combien de rangs la virgule a bougé dans chaque nombre.',
                'Mêmes rangs, même sens : **égal**. Sinon : **différent**.',
            ],
            svg: SVG_TRIER,
        },
    ],
};

// ═══════════════════════════════════════════════════════════
// Fiche 3 — Multiplier le moins possible
// ═══════════════════════════════════════════════════════════

const BTN_E = 'cp901rejouerE', BTN_F = 'cp901rejouerF';

/**
 * Un diviseur, l'essai qui échoue, l'essai qui réussit.
 *
 * PAS DE FRACTION ici : l'énoncé du QCM n'en contient aucune (« on multiplie les
 * deux nombres par un même entier pour que le diviseur devienne entier ; quel
 * est le plus petit entier possible ? »). La première version écrivait le
 * diviseur en fraction, ce qui ajoutait une représentation absente de l'exercice
 * et relevant d'un autre chapitre — et, formulée sans l'étape de simplification,
 * menait tout droit à 100 sur 2,25, l'un des distracteurs (retours utilisateur,
 * 2026-08-02).
 *
 * La méthode montrée reste celle de l'énoncé : on essaie, on s'arrête au premier
 * entier qui marche. Vérifié sur les 30 diviseurs du vivier — elle ne se trompe
 * jamais, mais demande jusqu'à 9 essais dans le cas ×10 (un quart des tirages),
 * d'où le raccourci par la fin du nombre porté par les `lignes`.
 *
 * `rate` vaut null quand ×2 marche du premier coup.
 */
const bandeEssai = (y, dec, rate, reussi) =>
    `<rect x="14" y="${y}" width="272" height="40" rx="7" fill="#f8fafc" stroke="#cbd5e1" stroke-width="1.2"/>` +
    `<text x="26" y="${y + 26}" font-size="13.5" font-weight="bold" fill="${ENCRE}">${dec}</text>` +
    (rate
        ? croix(76, y + 27) + `<text x="92" y="${y + 26}" font-size="11" fill="${GRIS}">${rate}</text>`
        : '') +
    coche(158, y + 27) +
    `<text x="174" y="${y + 26}" font-size="12.5" font-weight="bold" fill="${ORANGE}">${reussi}</text>`;

const SVG_MULTIPLICATEUR = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 224" width="290" height="217" role="img"
     aria-label="Quatre diviseurs, et pour chacun l'essai qui échoue puis celui qui réussit. Pour 0,5, multiplier par 2 donne 1 du premier coup. Pour 0,25, multiplier par 2 donne 0,5 qui n'est pas entier, tandis que multiplier par 4 donne 1. Pour 0,2, multiplier par 2 donne 0,4 qui n'est pas entier, tandis que multiplier par 5 donne 1. Pour 2,3, multiplier par 2 donne 4,6 qui n'est pas entier, tandis que multiplier par 10 donne 23. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  ${titre('Combien multiplier ?')}
  ${bouton(BTN_E, 210, 4)}
  ${etape(0.06, BTN_E, bandeEssai(32, '0,5', null, '× 2 = 1'))}
  ${etape(0.26, BTN_E, bandeEssai(78, '0,25', '× 2 = 0,5', '× 4 = 1'))}
  ${etape(0.46, BTN_E, bandeEssai(124, '0,2', '× 2 = 0,4', '× 5 = 1'))}
  ${etape(0.66, BTN_E, bandeEssai(170, '2,3', '× 2 = 4,6', '× 10 = 23'))}
</svg>`.trim();

const SVG_APPLIQUER = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 180" width="290" height="174" role="img"
     aria-label="Application au calcul 27 divisé par 7,5. On multiplie par 2 pour que 7,5 devienne entier, ce qui donne 54 divisé par 15, et 54 divisé par 15 égale 3,6. En bas, une remarque : multiplier par 10 aurait donné 270 divisé par 75, donc plus de chiffres à poser. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  ${titre('J\'applique aux deux')}
  ${bouton(BTN_F, 210, 4)}
  ${etape(0.06, BTN_F,
    `<text x="14" y="48" font-size="11.5" fill="${ARDOISE}">Pour que 7,5 devienne entier : <tspan font-weight="bold" fill="${ORANGE}">× 2</tspan></text>`)}
  ${etape(0.24, BTN_F, `<text x="14" y="82" font-size="13.5" font-weight="bold" fill="${ENCRE}">27 ÷ 7,5</text>`)}
  ${etape(0.36, BTN_F, fleche(86, 126, 78, '× 2'))}
  ${etape(0.48, BTN_F, `<text x="136" y="82" font-size="13.5" font-weight="bold" fill="${BLEU}">54 ÷ 15</text>`)}
  ${etape(0.62, BTN_F,
    `<text x="14" y="112" font-size="13.5" font-weight="bold" fill="${ENCRE}">54 ÷ 15 = <tspan fill="${ORANGE}">3,6</tspan></text>` + coche(140, 113))}
  ${etape(0.74, BTN_F, filet(128))}
  ${etape(0.82, BTN_F,
    `<text x="14" y="150" font-size="10.5" fill="${GRIS}">× 10 aurait donné 270 ÷ 75</text>` +
    `<text x="14" y="168" font-size="10.5" fill="${GRIS}">plus de chiffres, plus d'erreurs</text>`)}
</svg>`.trim();

/** Page commune au QCM et au texte à trous : trouver le multiplicateur. */
const PAGE_MULTIPLICATEUR = {
    titre: 'Le plus petit multiplicateur',
    court: 'Je cherche le plus petit entier qui fait disparaître la virgule du diviseur.',
    // La ligne 2 affirmait « dans cet exercice, seuls 2, 4, 5 et 10 peuvent
    // convenir » (revue 2026-08-02). Vrai du vivier de tirage, FAUX en maths :
    // 0,125 demande ×8, 0,05 demande ×20. Une fiche est un mini-cours, elle doit
    // rester vraie hors de l'exercice — sinon elle installe une certitude qui se
    // retourne contre l'élève au contrôle. C'était en outre un conseil méta
    // (« voici les réponses possibles »), qui vaut aussi pour le QCM partageant
    // cette page, où la question EST « quel est le plus petit entier ? ».
    lignes: [
        '**0,25** : ×2 donne 0,5, non. ×4 donne **1**, oui. La réponse est **4**.',
        'Je teste dans l\'ordre : **2**, puis **4**, **5**, **10**. Je m\'arrête au premier qui marche.',
    ],
    svg: SVG_MULTIPLICATEUR,
};

/** Le QCM s'arrête au multiplicateur : une seule page. */
const FICHE_MOINS_QCM = { pages: [PAGE_MULTIPLICATEUR] };

const FICHE_MOINS_TROU = {
    pages: [
        PAGE_MULTIPLICATEUR,
        {
            titre: 'J\'applique aux deux nombres',
            court: 'Le multiplicateur s\'applique aux deux nombres de la division.',
            lignes: [
                '**27 ÷ 7,5** : ×2 des deux côtés donne 54 ÷ 15.',
                '54 ÷ 15 = **3,6**. Le quotient n\'a pas changé.',
            ],
            svg: SVG_APPLIQUER,
        },
    ],
};

// ═══════════════════════════════════════════════════════════
// Fiche 4 — Répartir en récipients
// ═══════════════════════════════════════════════════════════

const BTN_G = 'cp901rejouerG', BTN_H = 'cp901rejouerH';

/** Douze gobelets en deux rangées de six. */
const gobelets = () => {
    let out = '';
    for (let i = 0; i < 12; i++) {
        const x = 120 + (i % 6) * 22;
        const y = i < 6 ? 42 : 70;
        out += `<rect x="${x}" y="${y}" width="18" height="22" rx="2" fill="#dbeafe" stroke="${BLEU}" stroke-width="1.2"/>`;
    }
    return out;
};

const SVG_REMPLIR = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 194" width="290" height="188" role="img"
     aria-label="Un grand récipient de 4,2 litres est vidé dans de petits gobelets de 0,35 litre chacun. Douze gobelets sont dessinés en deux rangées de six. Le calcul apparaît : 4,2 divisé par 0,35 égale 420 divisé par 35, égale 12 gobelets. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  ${titre('Combien de gobelets ?')}
  ${bouton(BTN_G, 210, 4)}
  ${etape(0.06, BTN_G,
    `<rect x="20" y="36" width="54" height="76" rx="4" fill="#dbeafe" stroke="${BLEU}" stroke-width="1.8"/>` +
    `<text x="47" y="126" text-anchor="middle" font-size="11.5" font-weight="bold" fill="${BLEU}">4,2 L</text>`)}
  ${etape(0.20, BTN_G, fleche(84, 112, 74))}
  ${etape(0.32, BTN_G, gobelets())}
  ${etape(0.48, BTN_G,
    `<text x="186" y="110" text-anchor="middle" font-size="9.5" fill="${ARDOISE}">0,35 L chacun</text>`)}
  ${etape(0.58, BTN_G, filet(136))}
  ${etape(0.66, BTN_G,
    `<text x="14" y="160" font-size="13" font-weight="bold" fill="${ENCRE}">4,2 ÷ 0,35 = 420 ÷ 35</text>`)}
  ${etape(0.84, BTN_G,
    `<text x="14" y="182" font-size="13" font-weight="bold" fill="${ORANGE}">= 12 gobelets</text>`)}
</svg>`.trim();

const SVG_VERIF = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 176" width="290" height="170" role="img"
     aria-label="Deux chemins. Le mauvais, croix rouge : 420 divisé par 0,35 donne 1200, parce que la contenance n'a pas glissé. Le bon, coche verte : 420 divisé par 35 donne 12, les deux nombres ont glissé de deux rangs. En bas, la vérification : 12 multiplié par 0,35 égale 4,2 litres. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  ${titre('Le piège du glissement')}
  ${bouton(BTN_H, 210, 4)}
  ${etape(0.08, BTN_H,
    croix(16, 50) +
    `<text x="38" y="50" font-size="13" font-weight="bold" fill="${ROUGE}">420 ÷ 0,35 = 1 200</text>`)}
  ${etape(0.24, BTN_H,
    `<text x="38" y="68" font-size="9.5" fill="${ARDOISE}">la contenance n'a pas glissé</text>`)}
  ${etape(0.38, BTN_H, filet(86))}
  ${etape(0.48, BTN_H,
    coche(16, 114) +
    `<text x="38" y="114" font-size="13" font-weight="bold" fill="${VERT}">420 ÷ 35 = 12</text>`)}
  ${etape(0.64, BTN_H,
    `<text x="38" y="132" font-size="9.5" fill="${ARDOISE}">les deux ont glissé de 2 rangs</text>`)}
  ${etape(0.82, BTN_H,
    `<text x="38" y="162" font-size="12.5" font-weight="bold" fill="${BLEU}">12 × 0,35 = 4,2 L</text>`)}
</svg>`.trim();

const FICHE_BOUTEILLES = {
    pages: [
        {
            titre: 'Combien de récipients ?',
            court: 'Je cherche combien de fois la contenance tient dans le volume.',
            lignes: [
                '**4,2 L** en gobelets de **0,35 L** : je calcule 4,2 ÷ 0,35.',
                'Je glisse la virgule **autant de rangs** des deux : 420 ÷ 35 = **12**.',
            ],
            svg: SVG_REMPLIR,
        },
        {
            titre: 'Vérifier son résultat',
            court: 'Si je ne glisse que d\'un côté, le résultat est dix ou cent fois trop grand.',
            lignes: [
                '420 ÷ 0,35 donne 1 200 : la contenance n\'a pas glissé.',
                'Je vérifie toujours : 12 × 0,35 = 4,2 L. C\'est bon.',
            ],
            svg: SVG_VERIF,
        },
    ],
};

// ═══════════════════════════════════════════════════════════
// Fiche 5 — Le coefficient d'agrandissement
// ═══════════════════════════════════════════════════════════

const BTN_I = 'cp901rejouerI', BTN_J = 'cp901rejouerJ';

const SVG_AGRANDIR = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 184" width="290" height="178" role="img"
     aria-label="Un petit rectangle de 6,4 centimètres de long est agrandi en un plus grand de 8 centimètres de long. Une flèche marquée d'un point d'interrogation relie les deux. Le calcul apparaît : 8 divisé par 6,4 égale 80 divisé par 64, égale 1,25. Le coefficient d'agrandissement vaut donc 1,25. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  ${titre('Par combien multiplier ?')}
  ${bouton(BTN_I, 210, 4)}
  ${etape(0.06, BTN_I,
    `<rect x="26" y="62" width="102" height="48" fill="none" stroke="${BLEU}" stroke-width="1.8"/>` +
    `<text x="77" y="56" text-anchor="middle" font-size="10.5" font-weight="bold" fill="${BLEU}">6,4 cm</text>`)}
  ${etape(0.22, BTN_I, fleche(132, 146, 86, '× ?'))}
  ${etape(0.36, BTN_I,
    `<rect x="150" y="50" width="128" height="60" fill="none" stroke="${ORANGE}" stroke-width="1.8"/>` +
    `<text x="214" y="44" text-anchor="middle" font-size="10.5" font-weight="bold" fill="${ORANGE}">8 cm</text>`)}
  ${etape(0.54, BTN_I, filet(126))}
  ${etape(0.64, BTN_I,
    `<text x="14" y="150" font-size="13" font-weight="bold" fill="${ENCRE}">8 ÷ 6,4 = 80 ÷ 64 = 1,25</text>`)}
  ${etape(0.84, BTN_I,
    `<text x="14" y="172" font-size="11.5" font-weight="bold" fill="${ORANGE}">coefficient : 1,25</text>`)}
</svg>`.trim();

const SVG_DIVISER = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 170" width="290" height="164" role="img"
     aria-label="Deux réponses mises à l'épreuve. La mauvaise, croix rouge : 8 moins 6,4 égale 1,6, mais 6,4 multiplié par 1,6 donne 10,24 et non 8. La bonne, coche verte : 8 divisé par 6,4 égale 1,25, et 6,4 multiplié par 1,25 donne bien 8. En bas, un rappel : je teste toujours ma réponse. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  ${titre('Diviser, pas soustraire')}
  ${bouton(BTN_J, 210, 4)}
  ${etape(0.08, BTN_J,
    croix(16, 50) +
    `<text x="38" y="50" font-size="13" font-weight="bold" fill="${ROUGE}">8 − 6,4 = 1,6</text>`)}
  ${etape(0.24, BTN_J,
    `<text x="38" y="68" font-size="10" fill="${ARDOISE}">6,4 × 1,6 = 10,24, pas 8</text>`)}
  ${etape(0.40, BTN_J, filet(86))}
  ${etape(0.50, BTN_J,
    coche(16, 114) +
    `<text x="38" y="114" font-size="13" font-weight="bold" fill="${VERT}">8 ÷ 6,4 = 1,25</text>`)}
  ${etape(0.66, BTN_J,
    `<text x="38" y="132" font-size="10" fill="${ARDOISE}">6,4 × 1,25 = 8</text>`)}
  ${etape(0.84, BTN_J,
    `<text x="38" y="158" font-size="10.5" fill="${GRIS}">je teste toujours ma réponse</text>`)}
</svg>`.trim();

const FICHE_RECTANGLES = {
    pages: [
        {
            titre: 'Le coefficient d\'agrandissement',
            court: 'Le coefficient, c\'est la nouvelle longueur divisée par l\'ancienne.',
            lignes: [
                '**6,4 cm** devient **8 cm** : je calcule 8 ÷ 6,4.',
                'Je glisse de 1 rang : 80 ÷ 64 = **1,25**.',
            ],
            svg: SVG_AGRANDIR,
        },
        {
            titre: 'Diviser, pas soustraire',
            court: 'La différence dit ce qu\'on ajoute, pas par combien on multiplie.',
            lignes: [
                'Je teste ma réponse : 6,4 × **1,25** = 8. Le coefficient est bon.',
                '6,4 × 1,6 = 10,24, ce n\'est pas 8. Donc 1,6 est faux.',
            ],
            svg: SVG_DIVISER,
        },
    ],
};

const CP_901 = {
    transformer_texte_trou_saisie:      FICHE_TRANSFORMER,
    transformer_texte_trou_drag:        FICHE_TRANSFORMER,
    quotients_egaux_qcm_multiple:       FICHE_QUOTIENTS,
    quotients_egaux_tableau_vf:         FICHE_QUOTIENTS,
    quotients_egaux_classer:            FICHE_QUOTIENTS,
    // Deux fiches : le QCM s'arrête au multiplicateur, le texte à trous
    // l'applique ensuite. PAGE_MULTIPLICATEUR est le seul objet commun.
    multiplier_moins_qcm_unique:        FICHE_MOINS_QCM,
    multiplier_moins_texte_trou_saisie: FICHE_MOINS_TROU,
    bouteilles_texte_trou_saisie:       FICHE_BOUTEILLES,
    bouteilles_qcm_unique:              FICHE_BOUTEILLES,
    rectangles_texte:                   FICHE_RECTANGLES,
    rectangles_qcm_unique:              FICHE_RECTANGLES,
};

export default CP_901;
